import {
    Btx,
    BTX_BNT,
    MAP_SALESCHANNEL_TO_VIRTUAL_ID,
    MAP_TARIFF_ID_TO_SALESCHANNEL,
    RedplusTariff,
    RedTariff,
    SAILS_BNT_STORAGE,
    SAILS_PARAM_SALESCHANNEL,
    SalesChannel,
    SALESCHANNEL_CONSUMER,
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_REDPLUS,
    URL_PARAM_ATOMIC_ID,
    URL_PARAM_GIGAKOMBI,
    URL_PARAM_SUBGROUP_ID,
    YoungTariff,
} from '@vfde-sails/constants';
import {
    getHardwareOnlyFromSails,
    getSessionStorageItemJson,
} from '@vfde-sails/storage';
import { getQueryParam } from '@vfde-sails/utils';
import {
    getAtomicIdFromSails,
    getDeviceIdFromSails,
    getGigakombiFromSails,
    getInsuranceFromSails,
    getRedPlusIdsFromSails,
    getRestlaufzeitFromSails,
    getSalesChannelFromSails,
    getSubscriptionIdFromSails,
    getTauschbonusFromSails,
    getTradeInFromSails,
    SailsBntStorage,
} from '@vfde-sails/storage';
import { URL_PARAM_DEVICE_ID } from '../container/App/constants';
import {
    getAllDiscounts,
    GetAllDiscountsOptions,
} from '@vfde-sails/page-options';

/**
 * Customer configuration
 */
export interface CustomerConfig {
    /* eslint-disable jsdoc/require-jsdoc */
    salesChannel: SalesChannel;
    btx: Btx;
    subscriptionId?: RedTariff | YoungTariff;
    atomicId?: string;
    deviceId?: string;
    discounts: string[];
    insurance: string | null;
    redPlusIds: string[];
    /* eslint-enable jsdoc/require-jsdoc */
}

/**
 * Get customer config
 */
export default function getCustomerConfig (): CustomerConfig {
    const storage = getSessionStorageItemJson<SailsBntStorage>(SAILS_BNT_STORAGE) || ({} as SailsBntStorage);
    const salesChannel = getSaleschannel(storage);
    const isHardwareOnly = getHardwareOnlyFromSails(storage);
    const subscriptionId = (!isHardwareOnly && getSubscriptionId(salesChannel, storage)) || null;
    const atomicId = getActiveAtomicId(storage);
    const deviceId = getDeviceId(storage);
    const discounts = getAllDiscounts({
        salesChannel,
        deviceId: deviceId ?? null,
        subscriptionIds: [subscriptionId].filter(Boolean) as GetAllDiscountsOptions['subscriptionIds'],
        isGigakombi: getGigakombi(salesChannel, storage),
        isTradeIn: getTradeInFromSails(storage),
        isTauschbonus: !!deviceId && getTauschbonusFromSails(storage),
        isRestlaufzeit: getRestlaufzeitFromSails(storage),
    });
    const redPlusIds: RedplusTariff[] = [];

    if (subscriptionId) {
        redPlusIds.push(...getRedPlusIdsFromSails(storage));
    }

    return {
        salesChannel,
        btx: BTX_BNT as Btx,
        ...(subscriptionId && { subscriptionId }),
        ...(atomicId && { atomicId }),
        ...(deviceId && { deviceId }),
        discounts,
        insurance: getInsuranceFromSails(storage),
        redPlusIds,
    };
}

/**
 * Get the sales channel from given subscription ID. Returns default "consumer".
 */
const getSaleschannel = (storage: SailsBntStorage | null): SalesChannel =>
    getSalesChannelFromUrl() ?? (storage && getSalesChannelFromSails(storage)) ?? SALESCHANNEL_CONSUMER;

/**
 * Get sales channel from URL
 */
const getSalesChannelFromUrl = (): SalesChannel | undefined => {
    const salesChannelFromUrl = getQueryParam(SAILS_PARAM_SALESCHANNEL) as string | undefined;

    if (salesChannelFromUrl && Object.keys(MAP_SALESCHANNEL_TO_VIRTUAL_ID).indexOf(salesChannelFromUrl) !== -1) {
        return salesChannelFromUrl as SalesChannel;
    }
};

const getSubscriptionId = (salesChannel: SalesChannel, storage: SailsBntStorage): RedTariff | YoungTariff | undefined =>
    getSubscriptionFromUrl(salesChannel) ?? getSubscriptionIdFromSails(storage, salesChannel) as RedTariff | YoungTariff | undefined;

/**
 * Get subscription ID from URL
 */
const getSubscriptionFromUrl = (salesChannel: SalesChannel): RedTariff | YoungTariff | undefined => {
    const urlParamSubGroupId = getQueryParam(URL_PARAM_SUBGROUP_ID) as RedTariff | YoungTariff;

    if (
        urlParamSubGroupId
        && urlParamSubGroupId in MAP_TARIFF_ID_TO_SALESCHANNEL
        && MAP_TARIFF_ID_TO_SALESCHANNEL[urlParamSubGroupId].indexOf(salesChannel) !== -1
    ) {
        return urlParamSubGroupId as RedTariff | YoungTariff;
    }
};

/**
 * Get device ID either from storage (where it can be a number or false) or from URL (number or undefined)
 * @returns string Atomic ID or undefined
 */
const getActiveAtomicId = (storage: SailsBntStorage | null): string | undefined => {
    const atomicIdFromUrl = getQueryParam(URL_PARAM_ATOMIC_ID) as string | undefined;

    if (atomicIdFromUrl) {
        return atomicIdFromUrl;
    }

    if (!storage) {
        return;
    }

    const atomicIdFromStorage = getAtomicIdFromSails(storage);

    return atomicIdFromStorage ? `${atomicIdFromStorage}` : undefined;
};

const getDeviceId = (storage: SailsBntStorage | null): string | undefined => {
    const deviceIdFromUrl = getQueryParam(URL_PARAM_DEVICE_ID) as string | undefined;

    if (deviceIdFromUrl) {
        return deviceIdFromUrl;
    }

    if (!storage) {
        return;
    }

    const deviceId = getDeviceIdFromSails(storage) as string | undefined; // BNT storage only has string | undefined as return types

    return deviceId || undefined;
};

/**
 * Get gigakombi from session storage
 */
const getGigakombi = (salesChannel: string, storage: SailsBntStorage): boolean => {
    if (salesChannel === SALESCHANNEL_FAMILY_FRIENDS || salesChannel === SALESCHANNEL_REDPLUS) {
        return false;
    }

    return getGigakombiFromUrl() ?? getGigakombiFromSails(storage);
};

const getGigakombiFromUrl = (): true | undefined => {
    const gigakombiParam = getQueryParam(URL_PARAM_GIGAKOMBI);

    return (gigakombiParam === '1' || gigakombiParam === 'true' || undefined) ?? (!!getGigakombiEligibilityToken() || undefined);
};

/**
 * Get eligibility token
 */
const getGigakombiEligibilityToken = (): string | undefined =>
    getQueryParam('token') as string | undefined // TODO: move to constants repo
;
